import React, { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useLang } from "../i18n/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import { BridgeMark } from "../components/BridgeMotif";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";

export default function Register() {
  const { t, lang } = useLang();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const roleParam = params.get("role");
  // "professional" is the public generic alias for the internal "psychologist" role.
  // Deep links using role=psychologist keep working; profession is chosen later in onboarding.
  const preset = roleParam === "professional" ? "psychologist"
    : ["client", "psychologist", "organizer"].includes(roleParam) ? roleParam : "";
  const [form, setForm] = useState({
    name: "", email: "", password: "",
    role: preset,
  });
  const [accept, setAccept] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.role) { setError(t("auth.choose_participation")); return; }
    if (!accept) { setError(t("auth.accept_terms_pre")); return; }
    setBusy(true);
    try {
      const u = await register({ ...form, accept_terms: true, accept_privacy: true, lang, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone });
      if (u?.role === "organizer") navigate("/organizador");
      else if (u?.role === "client") navigate("/profesionales");
      else navigate("/unirme");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto flex max-w-md flex-col items-center px-4 py-16">
      <BridgeMark size={40} />
      <h1 className="mt-4 font-display text-2xl font-bold tracking-tight text-slateink" data-testid="register-title">
        {preset === "psychologist" ? t("auth.register_professional_title")
          : preset === "organizer" ? t("auth.register_org_title")
          : preset === "client" ? t("auth.register_client_title")
          : t("auth.register_title")}
      </h1>
      <form onSubmit={submit} className="mt-8 w-full space-y-4 rounded-2xl border border-border bg-white p-7" data-testid="register-form">
        {error && <p className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="register-error">{error}</p>}
        {!preset && (
          <div>
            <Label>{t("auth.participate_q")}</Label>
            <div className="mt-2 grid grid-cols-1 gap-2">
              {[
                ["client", t("auth.participate_client")],
                ["psychologist", t("auth.participate_professional")],
                ["organizer", t("auth.participate_org")],
              ].map(([v, label]) => (
                <button type="button" key={v} onClick={() => set("role", v)} data-testid={`role-${v}`}
                  className={`rounded-xl border px-4 py-3 text-left text-sm font-medium transition-colors ${form.role === v ? "border-terracotta bg-terracotta-light text-terracotta" : "border-border text-slateink/70"}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}
        <div><Label>{t("common.name")}</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} required data-testid="register-name" /></div>
        <div><Label>{t("common.email")}</Label><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required data-testid="register-email" /></div>
        <div><Label>{t("common.password")}</Label><Input type="password" value={form.password} onChange={(e) => set("password", e.target.value)} required minLength={6} data-testid="register-password" /></div>
        <label className="flex items-start gap-2.5 cursor-pointer pt-1">
          <Checkbox checked={accept} onCheckedChange={(v) => setAccept(!!v)} data-testid="register-accept-terms" className="mt-0.5" />
          <span className="text-sm text-slateink/80">
            {t("auth.accept_terms_pre")}{" "}
            <Link to="/legal/terminos" target="_blank" className="text-terracotta hover:underline">{t("auth.terms_link")}</Link>{" "}
            {t("auth.and")}{" "}
            <Link to="/legal/privacidad" target="_blank" className="text-terracotta hover:underline">{t("auth.privacy_link")}</Link>.
          </span>
        </label>
        {form.role === "organizer" && <p className="rounded-lg bg-cream px-3 py-2 text-xs text-muted-foreground">{t("auth.organizer_pending")}</p>}
        <Button type="submit" disabled={busy || !form.role} className="w-full rounded-full bg-terracotta hover:bg-terracotta-hover" data-testid="register-submit">
          {busy ? t("auth.creating") : t("auth.register_btn")}
        </Button>
      </form>
      <p className="mt-6 text-sm text-muted-foreground">
        {t("auth.have_account")}{" "}
        <Link to="/login" className="font-medium text-terracotta hover:underline" data-testid="go-login">{t("auth.login_here")}</Link>
      </p>
    </div>
  );
}
